<template>
    <page-layout
        :readonly="isBriefAccepted && !isAdditionalOrder"
        :hide-footer="isBriefAccepted && !isAdditionalOrder"
    >
        <template slot="header">
            <step-header
                :current-step="stepNumber"
                :total-steps="totalSteps"
                :title="pageTitle"
                :use-exit-modal="!isBriefAccepted && exitConfirmationRequired"
                :skip-steps="isBriefAccepted"
            />
        </template>
        <template slot="default">
            <g-loader v-if="isLoading" />
            <template v-else>
                <div class="margin-bottom-large">
                    <g-typography tag="p" :value="shippingDescription" class="break-new-line" />
                </div>
                <template v-if="hasCompensationMethod">
                    <error-message-card v-if="errorMessage" class="margin-bottom-normal" :message="errorMessage" />
                    <step-error v-if="step && step.error" class="margin-bottom-normal" :step="step" />

                    <g-form>
                        <g-form-item :required="true" label="Email" :errors="errors.email">
                            <g-input v-model="address.email" size="large" :disabled="!isBriefEditable && !isAdditionalOrder" />
                        </g-form-item>
                        <g-form-item :required="true" label="First Name" :errors="errors.first_name">
                            <g-input v-model="address.first_name" size="large" :disabled="!isBriefEditable && !isAdditionalOrder" />
                        </g-form-item>
                        <g-form-item :required="true" label="Last Name" :errors="errors.last_name">
                            <g-input v-model="address.last_name" size="large" :disabled="!isBriefEditable && !isAdditionalOrder" />
                        </g-form-item>
                        <g-form-item :required="true" label="Address" :errors="errors.street">
                            <g-input v-model="address.street" size="large" :disabled="!isBriefEditable && !isAdditionalOrder" />
                        </g-form-item>
                        <g-form-item :required="false" label="Apt / Suite" :errors="errors.street_2">
                            <g-input v-model="address.street_2" size="large" :disabled="!isBriefEditable && !isAdditionalOrder" />
                        </g-form-item>
                        <g-form-item :required="true" label="Country" :errors="errors.country">
                            <g-select
                                v-model="address.country"
                                class="notranslate"
                                show-search
                                option-filter-prop="children"
                                :loading="loadingCountries"
                                :filter-option="filterLocation"
                                size="large"
                                :disabled="!isBriefEditable && !isAdditionalOrder"
                                style="width: 100%"
                                @change="() => address.state = null"
                            >
                                <g-select-option v-for="country in countries" :key="country.id" :value="country.name" class="notranslate">
                                    {{ country.name }}
                                </g-select-option>
                            </g-select>
                        </g-form-item>
                        <g-form-item :required="true" label="City" :errors="errors.city">
                            <g-input v-model="address.city" size="large" :disabled="!isBriefEditable && !isAdditionalOrder" />
                        </g-form-item>
                        <g-form-item v-if="provinces.length > 0" :required="true" :label="stateLabel" :errors="errors.state">
                            <g-select
                                v-model="address.state"
                                class="notranslate"
                                show-search
                                option-filter-prop="children"
                                :loading="loadingProvinces"
                                :filter-option="filterLocation"
                                size="large"
                                :disabled="!isBriefEditable && !isAdditionalOrder"
                                style="width: 100%"
                            >
                                <g-select-option v-for="province in provinces" :key="province.id" :value="province.name" class="notranslate">
                                    {{ province.name }}
                                </g-select-option>
                            </g-select>
                        </g-form-item>
                        <g-form-item :required="isZipRequired" :label="zipLabel" :errors="errors.zip">
                            <g-input v-model="address.zip" size="large" :disabled="!isBriefEditable && !isAdditionalOrder" />
                        </g-form-item>
                        <g-form-item :required="isPhoneNumberRequired" label="Phone Number" :errors="errors.phone_number">
                            <g-input v-model="address.phone_number" size="large" :disabled="!isBriefEditable && !isAdditionalOrder" />
                        </g-form-item>
                    </g-form>
                </template>
            </template>
        </template>
        <template slot="footer">
            <step-footer
                v-if="isBriefEditable"
                :is-next-disabled="isDisabled"
                :loading="isCompletingBrief"
                :step-completed="isCompleted"
                :current-step="stepNumber"
                :total-steps="totalSteps"
                :use-exit-modal="!isBriefAccepted && exitConfirmationRequired"
                @go-next-step="goNextStep"
                @go-previous-step="goPreviousStep"
            />

            <g-button
                v-if="isAdditionalOrder"
                :loading="isCompletingBrief"
                :disabled="isDisabled"
                size="xlarge"
                shape="round"
                theme="live"
                type="primary"
                class="g-w-full"
                @click="goNextStep"
            >Add Order</g-button>
        </template>
    </page-layout>
</template>

<script>
    import ErrorMessageCard from './partials/ErrorMessageCard';
    import PageLayout from '../../layouts/PageLayout';
    import {get, pick, cloneDeep} from 'lodash';
    import StepFooter from './partials/StepFooter';
    import ActivationBriefMixin from './mixins/ActivationBriefMixin';
    import StepError from './partials/StepError';
    import StepHeader from './partials/StepHeader';
    import {getCustomizableText} from '../../../helpers/activations/default-texts';
    import ExitConfirmationMixin from './mixins/ExitConfirmationMixin';

    export default {
        name:       'ActivationBriefShippingView',
        components: {
            StepHeader,
            StepError,
            StepFooter,
            ErrorMessageCard,
            PageLayout,
        },
        mixins: [
            ActivationBriefMixin,
            ExitConfirmationMixin,
        ],
        props: {
            brief: {
                type:    Object,
                default: null,
            },
            contact: {
                type:    Object,
                default: null,
            },
            step: {
                type:    Object,
                default: null,
            },
            isLoading: {
                type:    Boolean,
                default: false,
            },
            isCompletingBrief: {
                type:    Boolean,
                default: false,
            },
            isAdditionalOrder: {
                type:    Boolean,
                default: false,
            },
            errors: {
                type:    Object,
                default: () => ({}),
            },
            errorMessage: {
                type:    String,
                default: '',
            },
            countries: {
                type:     Array,
                required: true,
            },
            provinces: {
                type:     Array,
                required: false,
                default:  () => [],
            },
            loadingCountries: {
                type:    Boolean,
                default: false,
            },
            loadingProvinces: {
                type:    Boolean,
                default: false,
            },
        },
        data() {
            return {
                form:    null,
                address: {
                    email:        null,
                    street:       null,
                    street_2:     null,
                    country:      null,
                    city:         null,
                    state:        null,
                    zip:          null,
                    first_name:   null,
                    last_name:    null,
                    phone_number: null,
                },
                initialAddress: null,
            };
        },
        watch: {
            address: {
                deep: true,
                handler(value) {
                    this.$emit('country-updated', value.country);
                },
            },
            contact() {
                this.prePopulateAddressFromContact();
            },
        },
        computed: {
            pageTitle() {
                return this.isAdditionalOrder ? 'Verify Address' : 'Shipping Information';
            },
            stepNumber() {
                return get(this, 'step.meta.stepNumber', 0);
            },
            totalSteps() {
                return get(this, 'step.meta.totalSteps', 0);
            },
            isDisabled() { //TODO: replace with Ant form validation if possible
                return !(this.address.email
                    && this.address.street
                    && this.address.country
                    && this.address.city
                    && (this.provinces.length > 0 ? this.address.state : true)
                    && (this.isZipRequired ? this.address.zip : true)
                    && (this.isPhoneNumberRequired ? this.address.phone_number : true)
                    && this.address.first_name
                    && this.address.last_name);
            },
            shippingDescription() {
                return getCustomizableText(this.brief, 'products_shipping_information_description');
            },
            stateLabel() {
                return this.selectedCountryData && this.selectedCountryData.province_label ? this.selectedCountryData.province_label : 'Province';
            },
            selectedCountryData() {
                if (!this.address.country) {
                    return null;
                }

                return this.countries.find(country => country.name === this.address.country);
            },
            selectedStateData() {
                if (!this.address.state) {
                    return null;
                }

                return this.provinces.find(state => state.name === this.address.state);
            },
            zipLabel() {
                return this.address.country && this.address.country === 'United States' ? 'Zipcode' : 'Postcode';
            },
            isZipRequired() {
                return this.selectedCountryData ? this.selectedCountryData.has_postal_code : true;
            },
            isPhoneNumberRequired() {
                return !!get(this, 'brief.brand.ecommerce_account.id');
            },
            isCompleted() {
                return get(this, 'step.is_complete', false);
            },
            initialStepState() {
                return this.initialAddress;
            },
            currentStepState() {
                return this.address;
            },
            hasCompensationMethod() {
                return get(this, 'brief.has_module_products', false);
            },
            stepPayload() {
                return {
                    country_code: this.selectedCountryData ? this.selectedCountryData.code : null,
                    state_code:   this.selectedStateData ? this.selectedStateData.code : null,
                    ...this.address,
                };
            },
        },

        methods: {
            goNextStep() {
                this.$emit('go-next-step', this.step, this.stepPayload);
            },
            goPreviousStep() {
                this.$emit('go-previous-step', this.step, this.stepPayload);
            },
            prePopulateAddressFromContact() {
                if (this.contact && this.contact.primary_address) {
                    this.$emit('country-updated', this.contact.primary_address.country);

                    this.address = pick(this.contact.primary_address, Object.keys(this.address));

                    this.updateInitialState();
                }
            },
            filterLocation(input, option) {
                return (
                    option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
            },
            updateInitialState() {
                this.initialAddress = cloneDeep(this.address);
            },
        },
        created() {
            this.prePopulateAddressFromContact();
        },
    };
</script>
